import {
    closestCenter,
    DndContext,
    DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {arrayMove, SortableContext, verticalListSortingStrategy} from "@dnd-kit/sortable";
import * as React from "react";
import {useCallback} from "react";

import {ListEditorContainer} from "./list-editor-container";

export interface IListEditorProps<T> {
    items: Array<IListEditorItem<T>>;
    onChange: (items: Array<IListEditorItem<T>>) => void;
    itemComponentFactory: (item: IListEditorItemProps<T>) => JSX.Element;
    className?: string;
}

export interface IListEditorItemProps<T> {
    item: IListEditorItem<T>;
    order: number;
    onChange: (item: IListEditorItem<T>) => void;
    onRemove: () => void;
}

export interface IListEditorItem<T> {
    key: string;
    data: T | null;
}

function hasData<T>(item: IListEditorItem<T>): boolean {
    return item.data !== null;
}

function updateItems<T>(
    items: Array<IListEditorItem<T>>,
    onChange: (items: Array<IListEditorItem<T>>) => void,
) {
    onChange(items.filter(hasData));
}

export function ListEditor<T>(props: IListEditorProps<T>) {
    const handleChangeItem = useCallback(
        (item: IListEditorItem<T>, index: number) => {
            const arr = props.items.slice(0);
            arr.splice(index, 1, item);
            updateItems(arr, props.onChange);
        },
        [props.items, props.onChange],
    );

    const handleRemoveItem = useCallback(
        (index: number) => {
            const arr = props.items.slice(0);
            arr.splice(index, 1);
            updateItems(arr, props.onChange);
        },
        [props.items, props.onChange],
    );

    const handleSortEnd = useCallback(
        (event: DragEndEvent) => {
            const {active, over} = event;
            if (!over || active.id === over.id) {
                return;
            }
            const oldIndex = props.items.findIndex((item) => item.key === active.id);
            const newIndex = props.items.findIndex((item) => item.key === over.id);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }
            const reordered = arrayMove(props.items, oldIndex, newIndex);
            updateItems(reordered, props.onChange);
        },
        [props.items, props.onChange],
    );

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleSortEnd}
        >
            <SortableContext
                items={props.items.map((item) => item.key)}
                strategy={verticalListSortingStrategy}
            >
                <ListEditorContainer
                    items={props.items}
                    itemComponentFactory={props.itemComponentFactory}
                    className={props.className}
                    onChangeItem={handleChangeItem}
                    onRemoveItem={handleRemoveItem}
                />
            </SortableContext>
        </DndContext>
    );
}
